import React, { useState, useRef, useEffect, useCallback } from "react";
import { FlexLeftCol, GoalStyles } from "styles/global-style";
import Ribbon from "components/Timeline/Ribbon";
import DateComponent from "components/Timeline/Date";
const clone = require("rfdc")();

export default function DatesGoals({ dates, goals, size, space_x, space_y, animationDone, addGoalCb, addPersonCb, editGoalCb, showParentCb, hideAddButton, viewMode, goalPopupOpen, windowStart, windowEnd, scrollerRef }) {
  const [loading, setLoading] = useState(true);
  const [goalsArr, setGoalsArr] = useState();
  const [height, setHeight] = useState();
  const [highlightDateIndex, setHighlightDateIndex] = useState(-1);
  const [viewLeft, setViewLeft] = useState(0); // content-x of the viewport's left edge (scrollLeft)
  const [viewWidth, setViewWidth] = useState(0); // viewport width (clientWidth), for right-edge clip checks
  const scrollRafId = useRef();

  const monthNameRef = useRef();
  const hoverContainerRef = useRef();
  const animationFrameId = useRef(); // minimize mouse move re-renders
  const timelineMenuOpen = useRef(false);
  const firstDayIndexes = useRef([]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  const buildGoals = useCallback(() => {
    // Build Ribbon positioning array, wait with y position
    let arr = [];
    let maxHeight = 0;
    const monthYearBoxHeight = 22; // Adjust based on the actual height of the month/year box

    // Get today's date once outside the loop
    const today = new Date().toISOString().split('T')[0];

    // Find x positions for the 1st day of each month
    firstDayIndexes.current = [];
    let latestMonth = null;
    for (let k = 0; k < dates?.length; k++) {
      const currMonth = dates[k].date.getMonthName();
      if (latestMonth !== currMonth && dates[k].date.getUTCDate() < 24) {
        firstDayIndexes.current.push({ index: k, monthName: currMonth });
      }
      latestMonth = currMonth;
    }

    for (var i = 0; i < goals?.length; i++) {
      const nameWidth = goals[i].name?.getTextWidth(null, GoalStyles[goals[i].type].fontSize) || 100;
      const goal = goals[i];
      const goalInRange = dates[0].date.getISODate() < goal.date && goal.date < dates[dates.length - 1].date.getISODate();
      const datesIndex = dates?.findIndex((v) => v.date.getISODate() >= goal.date);
      
      // Check if goal is in the past (before today)
      const isPastGoal = goal.date < today;
      

      if (!goalInRange) continue;

      // If weekends are hidden, place their goals in their void space between days
      let x = 0;
      if (dates[datesIndex].date.getISODate() !== goal.date) {
        // On weekend
        x = (dates[datesIndex].left + dates[datesIndex - 1].left) / 2;
      } else {
        // Weekdays (or weekends if set to visible in settings)
        x = dates[datesIndex].left;
      }

      arr.push({
        data: goal,
        x: x,
        index: datesIndex,
        width: nameWidth + GoalStyles[goals[i].type].padding * 2,
        height: GoalStyles[goals[i].type].height,
        isPastGoal: isPastGoal,
      });
    }

    // Add y component
    for (let j = 0; j < arr.length; j++) {
      let goal = arr[j];
      let y = 0;

      // Check if the current goal overlaps horizontally with the "box" of the 1st day of the month
      // BUT only check months that actually show the year
      const overlapsFirstDayBox = firstDayIndexes.current.some(({ index, monthName, showYear }) => {
        if (!showYear) return false; // Skip months that don't show the year
        const firstDayX = dates[index].left - 10;
        const monthWidth = monthName.getTextWidth(monthNameRef.current, 28) + 10;
        return goal.x < firstDayX + monthWidth && goal.x + goal.width > firstDayX;
      });

      // If it overlaps with a month that shows the year, apply vertical offset to avoid the month/year "box"
      if (overlapsFirstDayBox) {
        y = monthYearBoxHeight; // Start y position below the month/year "box"
      } else {
        y = 0;
      }

      // Filter relevant stack for overlaps
      const relevantStack = arr.filter((v, index) => {
        if (index < j) {
          // Only consider items that are placed before the current one
          if (v.x < goal.x + goal.width && v.x + v.width > goal.x) {
            return true;
          }
        }
        return false;
      });

      // Determine the possible y positions, starting from 0 or the offset if needed
      let possibleYPositions = [y]; // Start with 0 or offset based on overlap with the month/year "box"

      for (var k = 0; k < relevantStack.length; k++) {
        const rel = relevantStack[k];

        // We add a potential y position just below this ribbon
        possibleYPositions.push(rel.y + rel.height + space_y);
      }

      // Sort possibleYPositions to try the lowest positions first
      possibleYPositions.sort((a, b) => a - b);

      // Check each possible position to see if the current goal would fit
      for (let pos of possibleYPositions) {
        let fits = true;
        for (let overlap of relevantStack) {
          if (pos < overlap.y + overlap.height && pos + goal.height > overlap.y) {
            fits = false;
            break;
          }
        }
        if (fits) {
          y = pos;
          break;
        }
      }

      maxHeight = Math.max(maxHeight, y + goal.height);
      arr[j].y = y;
    }

    setHeight(maxHeight);
    setGoalsArr(arr);
  }, [goals, dates, space_y]);

  const handleEditGoal = useCallback(
    (id) => {
      editGoalCb(id);
    },
    [editGoalCb]
  );

  const handleMoveGoal = useCallback(
    (id, newIndex) => {
      const oldIndex = goalsArr.findIndex((g) => g.id === id);
      let newArr = clone(goalsArr);
      const goal = newArr.splice(oldIndex, 1);
      newArr.splice(newIndex, 0, goal[0]);
      setGoalsArr(newArr);
      editGoalCb(id, newIndex);
    },
    [goalsArr, setGoalsArr, editGoalCb]
  );

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  useEffect(() => {
    // Show + button on dates depending on mouse x position
    const handleMouseMove = (e) => {
      if (timelineMenuOpen.current) return;
      // We use requestAnimationFrame to throttle updates
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }

      animationFrameId.current = requestAnimationFrame(() => {
        if (hoverContainerRef.current) {
          const rect = hoverContainerRef.current.getBoundingClientRect();
          const xPosition = e.clientX - rect.left;
          // Calculate which div the mouse is over
          let newIndex = -1;
          for (let i = 0; i < dates?.length; i++) {
            const d = dates[i];
            if (d.left - 2 < xPosition && xPosition < d.right + 2) {
              newIndex = i;
              break;
            }
          }
          if (xPosition < 0 || newIndex === -1) {
            setHighlightDateIndex(null);
          } else if (newIndex !== highlightDateIndex) {
            setHighlightDateIndex(newIndex);
          }
        }
      });
    };

    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [size, space_x, dates, highlightDateIndex, goals]);

  useEffect(() => {
    if (goals && dates) {
      buildGoals();
    }
  }, [buildGoals, goals, dates]);

  useEffect(() => {
    if ((dates && goals && goalsArr && size && space_x, height)) {
      setLoading(false);
    }
  }, [dates, goals, goalsArr, size, space_x, height]);

  useEffect(() => {
    if (!loading) showParentCb(true);
  }, [loading, showParentCb, viewMode]);

  // When goal popup closes (e.g. Escape, blur, or delete-empty), reset so date + icon tracker works again
  useEffect(() => {
    if (goalPopupOpen === false) {
      timelineMenuOpen.current = false;
    }
  }, [goalPopupOpen]);

  // Track the viewport's left edge (scrollLeft) and width so flags and month
  // labels can be hidden when they would clip at either viewport edge.
  // rAF-throttled to one update per frame.
  useEffect(() => {
    const el = scrollerRef?.current;
    if (!el) return;
    const update = () => {
      if (scrollRafId.current) return;
      scrollRafId.current = requestAnimationFrame(() => {
        scrollRafId.current = null;
        setViewLeft(el.scrollLeft);
        setViewWidth(el.clientWidth);
      });
    };
    update();
    el.addEventListener("scroll", update, { passive: true });
    return () => {
      el.removeEventListener("scroll", update);
      if (scrollRafId.current) {
        cancelAnimationFrame(scrollRafId.current);
        scrollRafId.current = null;
      }
    };
  }, [scrollerRef]);

  // Sync to the actual scroll position whenever the geometry/window or goals
  // change (e.g. the initial today-aligned scroll on load), so clipped flags
  // are hidden correctly before the user scrolls.
  useEffect(() => {
    const el = scrollerRef?.current;
    if (el) {
      setViewLeft(el.scrollLeft);
      setViewWidth(el.clientWidth);
    }
  }, [scrollerRef, windowStart, windowEnd, goalsArr, loading]);

  //xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx

  return (
    <div style={{ display: "inline-block", position: "relative", width: "100%", paddingTop: 4 }}>
      <div
        style={{
          display: "inline-block",
          position: "relative",
          // Reserve room for the month/year labels (which sit above the date
          // row via translateY(-100%)) so the scroller's overflow-y:hidden does
          // not clip them when few/no goals are in view.
          height: loading ? 0 : Math.max(height || 0, 20) + 20,
          marginBottom: -2,
          visibility: loading ? "hidden" : "visible",
        }}
      >
        {goalsArr &&
          goalsArr.map((goal, k) => {
            // Sort goals so past goals are rendered first (lower z-index)
            const sortedGoals = [...goalsArr].sort((a, b) => {
              if (a.isPastGoal && !b.isPastGoal) return -1;
              if (!a.isPastGoal && b.isPastGoal) return 1;
              return 0;
            });
            const sortedIndex = sortedGoals.findIndex(g => g.data.id === goal.data.id);

            // Don't render flags that would clip at either edge of the
            // viewport; only fully visible flags are shown.
            const flagRight = goal.x + goal.width + 8; // pillar offset + skew slack
            if (goal.x < viewLeft || (viewWidth > 0 && flagRight > viewLeft + viewWidth)) return null;

            return (
              <Ribbon
                key={"goal" + goal.data.id}
                id={goal.data.id}
                dateIndex={goal.index}
                dates={dates}
                size={size}
                space_x={space_x}
                animationDone={animationDone}
                containerLeft={hoverContainerRef?.current?.getBoundingClientRect().left}
                name={goal.data.name}
                type={goal.data.type}
                color={goal.data.color}
                x={goal.x}
                y={goal.y + 4}
                z={goal.isPastGoal ? 100 - sortedIndex : 500 - sortedIndex}
                onClick={handleEditGoal}
                movedGoalCb={handleMoveGoal}
                disabled={goal.data?.disabled}
                isPastGoal={goal.isPastGoal}
              />
            );
          })}
      </div>

      <div ref={hoverContainerRef} style={{ height: size }}>
        {dates?.map((d, j) => {
          // Windowed virtualization: only mount date cells inside the visible
          // window (+buffer). j stays the absolute universe index.
          if ((windowStart != null && j < windowStart) || (windowEnd != null && j > windowEnd)) return null;
          const showMonth = firstDayIndexes.current.find((mon) => mon.index === j);
          const isWeekMode = d.isWeekMode;

          // Don't render month/year labels that would clip at either edge of
          // the viewport.
          let monthClipped = false;
          if (showMonth) {
            const label = viewMode === 'weeks' ? d.date.getMonthName(true) : showMonth.monthName;
            const labelWidth = label.getTextWidth(monthNameRef.current, 28);
            monthClipped = d.left < viewLeft || (viewWidth > 0 && d.left + labelWidth > viewLeft + viewWidth);
          }
          
          return (
            <div
              key={"date" + j}
              style={{
                position: "absolute",
                left: d.left,
                width: isWeekMode ? size * 2 : size,
                userSelect: "none",
              }}
            >
              {showMonth && !monthClipped && (
                <FlexLeftCol
                  className="timeline-month-year"
                  gap={0}
                  style={{ position: "absolute", transform: "translateY(-100%)", textShadow: "rgb(34, 34, 34) 1px 1px 3px", marginLeft: 1 }}
                >
                  {/* Show year only for first month or every January */}
                  {(j === 0 || d.date.getMonth() === 0) && (
                    <h5 style={{ fontSize: 12, marginBottom: -4 }}>{d.date.getFullYear()}</h5>
                  )}
                  <h4 ref={monthNameRef} style={{ fontSize: 22 }}>
                    {viewMode === 'weeks' ? d.date.getMonthName(true) : showMonth.monthName}
                  </h4>
                </FlexLeftCol>
              )}
              <DateComponent
                date={d.date}
                weekEnd={isWeekMode ? d.weekEnd : null}
                isWeekMode={isWeekMode}
                showButton={j === highlightDateIndex && !hideAddButton}
                size={size}
                addGoalCb={addGoalCb}
                addPersonCb={addPersonCb}
                dateClickCb={(isOpen) => (timelineMenuOpen.current = isOpen)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
